import { useMemo, useState } from "react"
import {
  Activity,
  Users,
  Layers,
  Clock,
  BarChart3,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { useLocale } from "../components/LocaleProvider"
import WorkerOverviewModal from "../components/WorkerOverviewModal"
import OperatorAnalyticsModal from "../components/OperatorAnalyticsModal"
import WorkerActivityReportModal from "../components/WorkerActivityReportModal"
import type { TranslationKey } from "../i18n/translations"
import { useWorkerActivity, useWorkerActivityHistory } from "../lib/queries"
import { formatDuration, formatRelative, formatTime, windowSeconds } from "../lib/format"
import type { WorkerActivityRecord, WorkerDailyRecord } from "../lib/api"

function StatusBadge({ online, t }: { online: boolean; t: (k: TranslationKey) => string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        online
          ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          : "bg-muted text-muted-foreground"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          online ? "bg-emerald-500" : "bg-muted-foreground/60"
        }`}
      />
      {t(online ? "activity_online" : "activity_offline")}
    </span>
  )
}

function WorkerActivity() {
  const { t } = useLocale()
  const { data: live, isLoading } = useWorkerActivity(10000)
  const [historyDays, setHistoryDays] = useState(30)
  const [heartbeatSort, setHeartbeatSort] = useState<"none" | "asc" | "desc">("none")
  const { data: history, isLoading: historyLoading } = useWorkerActivityHistory(historyDays)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)

  const rows: WorkerActivityRecord[] = useMemo(() => live ?? [], [live])
  const selected = useMemo(
    () => rows.find((r) => r.userId === selectedUserId) ?? null,
    [rows, selectedUserId],
  )

  const stats = useMemo(() => {
    const online = rows.filter((r) => r.online).length
    const tabs = rows.reduce((sum, r) => (r.online ? sum + r.tabCount : sum), 0)
    const firsts = rows.map((r) => r.todayFirstSeenAt).filter((v): v is string => Boolean(v))
    const lasts = rows.map((r) => r.todayLastSeenAt).filter((v): v is string => Boolean(v))
    const todayStart = firsts.length ? firsts.reduce((a, b) => (a < b ? a : b)) : null
    const todayEnd = lasts.length ? lasts.reduce((a, b) => (a > b ? a : b)) : null
    return { online, offline: rows.length - online, tabs, todayStart, todayEnd }
  }, [rows])

  const historyRows: WorkerDailyRecord[] = useMemo(() => history ?? [], [history])
  const sortedHistoryRows: WorkerDailyRecord[] = useMemo(() => {
    if (heartbeatSort === "none") return historyRows
    return [...historyRows].sort((a, b) =>
      heartbeatSort === "asc"
        ? a.heartbeatCount - b.heartbeatCount
        : b.heartbeatCount - a.heartbeatCount,
    )
  }, [historyRows, heartbeatSort])
  const maxHistorySeconds = useMemo(
    () => Math.max(1, ...historyRows.map((r) => windowSeconds(r.firstSeenAt, r.lastSeenAt))),
    [historyRows],
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground">
        {t("loading")}
      </div>
    )
  }

  const cards = [
    { label: t("activity_online_now"), value: String(stats.online), icon: Users },
    { label: t("activity_offline"), value: String(stats.offline), icon: Users },
    { label: t("activity_total_tabs"), value: String(stats.tabs), icon: Layers },
    {
      label: t("activity_today_window"),
      value:
        stats.todayStart && stats.todayEnd
          ? `${formatTime(stats.todayStart)}–${formatTime(stats.todayEnd)}`
          : "—",
      icon: Clock,
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <c.icon className="h-4 w-4" />
              {c.label}
            </div>
            <p className="mt-2 text-2xl font-semibold">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4" />
            {t("activity_live")}
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            10s
          </span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("activity_worker")}</TableHead>
              <TableHead className="w-32">{t("activity_status")}</TableHead>
              <TableHead className="w-24">{t("activity_tabs")}</TableHead>
              <TableHead className="w-32">{t("activity_last_seen")}</TableHead>
              <TableHead className="w-32">{t("activity_session")}</TableHead>
              <TableHead className="w-32">{t("activity_today_window")}</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {t("activity_empty")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell className="text-sm font-medium">
                    <div>{row.fullName}</div>
                    {row.browsers > 1 && (
                      <div className="text-xs text-muted-foreground">
                        {row.browsers} {t("activity_browsers")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge online={row.online} t={t} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{row.tabCount}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatRelative(row.lastSeenAt, t("activity_never"))}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDuration(row.sessionSeconds)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <div className="whitespace-nowrap">
                      {formatTime(row.todayFirstSeenAt)}–{formatTime(row.todayLastSeenAt)}
                    </div>
                    <div className="text-xs opacity-60">
                      {formatDuration(windowSeconds(row.todayFirstSeenAt, row.todayLastSeenAt))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => setSelectedUserId(row.userId)}
                      title={t("activity_open_overview")}
                      aria-label={t("activity_open_overview")}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      <BarChart3 className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="text-sm font-medium">{t("activity_history")}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReportOpen(true)}
              title={t("activity_report_title")}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <FileText className="h-3.5 w-3.5" />
              {t("activity_generate_report")}
            </button>
            <button
              onClick={() => setAnalyticsOpen(true)}
              title={t("activity_analytics_title")}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              {t("activity_team_analytics")}
            </button>
            <div className="flex items-center gap-1">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setHistoryDays(d)}
                  className={`h-7 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                    historyDays === d
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">{t("activity_date")}</TableHead>
              <TableHead>{t("activity_worker")}</TableHead>
              <TableHead className="w-40">{t("activity_work_window")}</TableHead>
              <TableHead className="w-32">
                <button
                  type="button"
                  onClick={() =>
                    setHeartbeatSort(
                      heartbeatSort === "none" ? "asc" : heartbeatSort === "asc" ? "desc" : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("activity_heartbeats")}
                  {heartbeatSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : heartbeatSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead className="w-48">{t("th_chart")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {historyLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : historyRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  {t("activity_empty")}
                </TableCell>
              </TableRow>
            ) : (
              sortedHistoryRows.map((row) => (
                <TableRow key={`${row.userId}-${row.date}`}>
                  <TableCell className="font-mono text-sm whitespace-nowrap">{row.date}</TableCell>
                  <TableCell className="text-sm font-medium">{row.fullName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDuration(windowSeconds(row.firstSeenAt, row.lastSeenAt))}
                    <span className="ml-2 text-xs opacity-60">
                      {formatTime(row.firstSeenAt)}–{formatTime(row.lastSeenAt)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.heartbeatCount}
                  </TableCell>
                  <TableCell>
                    <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{
                          width: `${(windowSeconds(row.firstSeenAt, row.lastSeenAt) / maxHistorySeconds) * 100}%`,
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <WorkerOverviewModal
        open={selectedUserId != null && selected != null}
        onOpenChange={(open) => {
          if (!open) setSelectedUserId(null)
        }}
        record={selected}
      />

      <OperatorAnalyticsModal open={analyticsOpen} onOpenChange={setAnalyticsOpen} />

      <WorkerActivityReportModal open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  )
}

export default WorkerActivity
